/**
 * The last project the user opened, remembered across visits. This is read during render, so it
 * goes through the synchronous preference snapshot in `@molecule/app-storage` (never `localStorage`
 * directly): swapping the bonded provider redirects it, and an absent or unreadable value reads as
 * "none" — not a crashed route.
 *
 * @module
 */

import { getPreferenceStore } from '@molecule/app-storage'

const KEY = 'molecule_last_project'

/**
 * Validates a stored project id.
 *
 * The value can be a string written by this store OR by the previous raw-string
 * helper (a bare uuid — the app's storage codec preserves those), and it can be
 * anything at all after a corrupt write.
 * @param value - The stored value.
 * @returns The id when it is a usable project id, otherwise null.
 */
export function parseLastProject(value: unknown): string | null {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,100}$/.test(value) ? value : null
}

/**
 * Reads the remembered project id.
 *
 * @returns The id, or null when none is stored or storage is unavailable.
 */
export function readLastProject(): string | null {
  return parseLastProject(getPreferenceStore().get<unknown>(KEY))
}

/** The storage key holding the last opened project id. */
export const LAST_PROJECT_KEY = KEY
